import React, { useState } from 'react';
import styles from './QuizDetails.module.scss';
import cx from 'classnames';
import { useAppSelector } from '../../../store';

// Icons
import ArrowIcon from '../../../assets/icons/arrow.svg';
import DownloadIcon from '../../../assets/icons/download.svg';
import CheckedIcon from '../../../assets/icons/checkbox-checked.svg';

interface QuizDetailsProps {
  notCareAnsweredIndex: number;
  isOpen?: boolean;
  onClose?: () => void;
}

const QuizDetails: React.FC<QuizDetailsProps> = ({ notCareAnsweredIndex, isOpen, onClose }) => {
  const [rotateCare, setRotateCare] = useState(true); // Open by default
  const [rotateLegal, setRotateLegal] = useState(false);
  const [rotateAuth, setRotateAuth] = useState(false);

  // Redux state
  const formData = useAppSelector((state) => state.formData);
  const personCare = useAppSelector((state) => state.quiz.personCare);
  const caregiverCare = useAppSelector((state) => state.quiz.caregiverCare);

  const {
    personCare: personCareData,
    caregiverCare: caregiverData,
    termsAndConditions: termsData,
  } = formData;

  // Get file names from quiz questions
  const getFileName = (questions: any[], questionKey: string) => {
    const question = questions.find((q) => q.questionKey === questionKey);
    return question?.fileName || 'Datei';
  };

  // Download file handler
  const downloadFile = (url: string, name: string) => {
    if (!url || url === '-') return;

    fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', name);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
      })
      .catch((err) => console.log(err));
  };

  // Render a list item with label and value
  const renderListItem = (label: string, value: string | undefined) => {
    if (!value || value === '-') return null;

    return (
      <li>
        <span className={styles.label}>{label}</span>
        <span className={styles.value}>{value}</span>
      </li>
    );
  };

  // Render a file item
  const renderFileItem = (label: string, url: string | undefined, fileName: string) => {
    if (!url || url === '-') return null;

    return (
      <li className={styles.fileItem}>
        <span className={styles.label}>{label}</span>
        <span className={styles.value}>
          <span className={styles.fileName}>{fileName}</span>
          <img
            src={DownloadIcon}
            alt="Download"
            className={styles.downloadIcon}
            onClick={() => downloadFile(url, fileName)}
          />
        </span>
      </li>
    );
  };

  // Check if we have any person care data
  const hasPersonCareData = notCareAnsweredIndex !== 0 && personCareData.lastName;

  // Check if we have caregiver data
  const hasCaregiverData = caregiverData.lastName && personCareData.legalRepresentation === 'Ja';

  // Check if we have terms data
  const hasTermsData = termsData.iHere === 'checked';

  // Don't render anything if no data is filled yet
  if (!hasPersonCareData) {
    return null;
  }

  return (
    <div className={cx(styles.details, { [styles.open]: isOpen })}>
      {/* Close button for tablet/mobile */}
      {onClose && (
        <button className={styles.closeBtn} onClick={onClose}>
          ×
        </button>
      )}
      {/* Person Care Section */}
      {hasPersonCareData && (
        <div className={styles.section}>
          <div className={styles.sectionHeader} onClick={() => setRotateCare(!rotateCare)}>
            <p>Pflegebedürftige Person</p>
            <img
              src={ArrowIcon}
              alt="Toggle"
              className={cx(styles.arrow, { [styles.rotate]: rotateCare })}
            />
          </div>

          {rotateCare && (
            <div className={styles.sectionContent}>
              <ul>
                {renderListItem('Name des Pflegebedürft...', personCareData.lastName)}
                {renderListItem('Vorname des Pflegebedü...', personCareData.name)}
                {renderListItem('Geburtsdatum', personCareData.birthday)}
                {renderListItem('Straße', personCareData.address)}
                {renderListItem('Hausnummer', personCareData.houseNumber)}
                {renderListItem('PLZ', personCareData.zipCode)}
                {renderListItem('Ort', personCareData.city)}
                {renderListItem('Telefonnummer', personCareData.phoneNumber)}
                {renderListItem('Krankenversicherung', personCareData.insurance)}
                {renderFileItem(
                  'Versicherungsnachweis',
                  personCareData.attachmentForInsuranceCard,
                  getFileName(personCare.questions, 'attachmentForInsuranceCard')
                )}
                {renderFileItem(
                  'Pflegegradbescheid',
                  personCareData.certificate,
                  getFileName(personCare.questions, 'certificate')
                )}
                {renderListItem('Gesetzliche Betreuung', personCareData.legalRepresentation)}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Caregiver Section */}
      {hasCaregiverData && (
        <div className={styles.section}>
          <div className={styles.sectionHeader} onClick={() => setRotateLegal(!rotateLegal)}>
            <p>Betreuer der pflegebedürftigen Person</p>
            <img
              src={ArrowIcon}
              alt="Toggle"
              className={cx(styles.arrow, { [styles.rotate]: rotateLegal })}
            />
          </div>

          {rotateLegal && (
            <div className={styles.sectionContent}>
              <ul>
                {renderListItem('Name', caregiverData.lastName)}
                {renderListItem('Vorname', caregiverData.name)}
                {renderListItem('Straße', caregiverData.address)}
                {renderListItem('Hausnummer', caregiverData.houseNumber)}
                {renderListItem('PLZ', caregiverData.zipCode)}
                {renderListItem('Ort', caregiverData.city)}
                {renderListItem('Telefonnummer', caregiverData.phoneNumber)}
                {renderFileItem(
                  'Betreuungsverfügung',
                  caregiverData.document,
                  getFileName(caregiverCare.questions, 'document')
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Terms Section */}
      {hasTermsData && (
        <div className={styles.section}>
          <div className={styles.sectionHeader} onClick={() => setRotateAuth(!rotateAuth)}>
            <p>Geschäftsbedingungen</p>
            <img
              src={ArrowIcon}
              alt="Toggle"
              className={cx(styles.arrow, { [styles.rotate]: rotateAuth })}
            />
          </div>

          {rotateAuth && (
            <div className={styles.sectionContent}>
              <ul>
                {termsData.iHere === 'checked' && (
                  <li>
                    <span className={styles.label}>Bevollmächtigung Verhinderungspflege</span>
                    <span className={styles.value}>
                      <img src={CheckedIcon} alt="✓" />
                    </span>
                  </li>
                )}
                {termsData.ConsentToTheData === 'checked' && (
                  <li>
                    <span className={styles.label}>Zustimmung Datenverarbeitung</span>
                    <span className={styles.value}>
                      <img src={CheckedIcon} alt="✓" />
                    </span>
                  </li>
                )}
                {termsData.iAgree === 'checked' && (
                  <li>
                    <span className={styles.label}>Alle Angaben freiwillig</span>
                    <span className={styles.value}>
                      <img src={CheckedIcon} alt="✓" />
                    </span>
                  </li>
                )}
                {termsData.iHave === 'checked' && (
                  <li>
                    <span className={styles.label}>Zustimmung Verarbeitung nach SGB</span>
                    <span className={styles.value}>
                      <img src={CheckedIcon} alt="✓" />
                    </span>
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuizDetails;
